import { Breadcrumb, BreadcrumbLink, BreadcrumbPage, Button } from '@ads/components-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type {
  RevisionGridFilters,
  RevisionGridItem,
  RevisionSort,
} from '@/features/exam-revision/types/revision';
import { ClassStudentAutocomplete } from '@/features/exam-revision/ui/molecules/ClassStudentAutocomplete';
import { DisciplineAutocomplete } from '@/features/exam-revision/ui/molecules/DisciplineAutocomplete';
import { RevisionTable } from '@/features/exam-revision/ui/organisms/RevisionTable';
import { DatePicker } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export interface RevisionListTemplateProps {
  readonly items: RevisionGridItem[];
  readonly filters: RevisionGridFilters;
  readonly onFilterChange: (patch: Partial<RevisionGridFilters>) => void;
  readonly onSearch: () => void;
  readonly onClear: () => void;
  readonly totalItems: number | undefined;
  readonly hasNextPage: boolean;
  readonly currentPage: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly sorting: RevisionSort[];
  readonly onSortingChange: (sorting: RevisionSort[]) => void;
  readonly onApprove: (item: RevisionGridItem) => void;
  readonly onTranslate: (item: RevisionGridItem) => void;
  readonly onEdit: (item: RevisionGridItem) => void;
  readonly onView: (item: RevisionGridItem) => void;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  /** Muda a cada "Limpar" para remontar os campos de filtro e garantir o reset visual. */
  readonly resetKey: number;
}

/**
 * Template da tela de listagem de provas para revisão.
 *
 * Renderiza o cabeçalho com breadcrumb e contagem total, um card de filtros
 * (disciplina, data e turma) e o card de tabela com tags de turma, status
 * colorido com indicador e paginação. Recebe dados e callbacks via props —
 * sem lógica de negócio interna.
 *
 * @param items - Provas exibidas na página atual
 * @param filters - Filtros atualmente aplicados
 * @param onFilterChange - Atualiza um subconjunto dos filtros
 * @param onSearch - Aplica os filtros
 * @param onClear - Limpa os filtros
 * @param totalItems - Total de provas (todas as páginas)
 * @param totalPages - Total de páginas
 * @param currentPage - Página atual (1-based)
 * @param onPageChange - Navega para uma página
 * @param sorting - Estado de ordenação atual das colunas
 * @param onSortingChange - Atualiza a ordenação das colunas
 * @param onApprove - Ação "Aprovar" de uma prova
 * @param onTranslate - Ação "Traduzir" de uma prova
 * @param onEdit - Ação "Editar" de uma prova
 * @param onView - Ação "Visualizar" de uma prova
 * @param isLoading - Estado de carregamento
 */
export function RevisionListTemplate({
  items,
  filters,
  onFilterChange,
  onSearch,
  onClear,
  totalItems,
  hasNextPage,
  currentPage,
  pageSize,
  onPageChange,
  sorting,
  onSortingChange,
  onApprove,
  onTranslate,
  onEdit,
  onView,
  isLoading,
  isError,
  onRetry,
  resetKey,
}: RevisionListTemplateProps) {
  const { t } = useTranslation('examRevision');

  return (
    <div className="flex flex-col gap-xs">
      <PageHeader
        title={t('list.title')}
        subtitle={
          totalItems !== undefined ? t('list.count', { count: totalItems }) : t('list.subtitle')
        }
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">{t('breadcrumb.home')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={'/exams' as never}>{t('breadcrumb.exams')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('breadcrumb.revision')}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      {/* Filters Card */}
      <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs md:flex-row md:items-stretch">
        {/* Disciplina e Turma empilhados, ocupando a maior parte da largura */}
        <div className="flex flex-1 flex-col gap-mini">
          <DisciplineAutocomplete
            key={`discipline-${resetKey}`}
            label={t('list.filters.discipline')}
            placeholder={t('list.filters.disciplinePlaceholder')}
            value={filters.disciplineId !== null ? String(filters.disciplineId) : ''}
            onChange={id => onFilterChange({ disciplineId: id ? Number(id) : null })}
          />
          <ClassStudentAutocomplete
            key={`classStudent-${resetKey}`}
            label={t('list.filters.classCode')}
            placeholder={t('list.filters.classCodePlaceholder')}
            value={filters.classStudentId !== null ? String(filters.classStudentId) : ''}
            onChange={id => onFilterChange({ classStudentId: id ? Number(id) : null })}
          />
        </div>
        {/* Data e ações */}
        <div className="flex flex-col justify-between gap-mini md:w-[260px]">
          <DatePicker
            key={`date-${resetKey}`}
            label={t('list.filters.date')}
            value={filters.date}
            onChange={v => onFilterChange({ date: v })}
          />
          <div className="flex gap-nano">
            <Button
              hierarchy="primary"
              size="md"
              leadingIcon={MagnifyingGlassIcon}
              onClick={onSearch}
            >
              {t('list.filters.search')}
            </Button>
            <Button hierarchy="tertiary" size="md" onClick={onClear}>
              {t('list.filters.clear')}
            </Button>
          </div>
        </div>
      </div>

      {/* Table com rodapé de paginação integrado */}
      <RevisionTable
        items={items}
        sorting={sorting}
        onSortingChange={onSortingChange}
        onApprove={onApprove}
        onTranslate={onTranslate}
        onEdit={onEdit}
        onView={onView}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        hasNextPage={hasNextPage}
        onPageChange={onPageChange}
      />
    </div>
  );
}
